import { useEffect, useState, useCallback } from "react";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import InvoicesTable from "../Components/Invoices/InvoicesTable";
import InvoicesForm from "../Components/Invoices/InvoicesForm";
import InvoicesHeader from "../Components/Invoices/InvoicesHeader";
import invoiceService from "../Services/invoiceService";
import { useAuth } from "../Auth/AuthContext";
import PublisherApiService from "../Services/PublisherApiService";
import propertyService from "../Services/propertyService";

const initialFormState = {
  publisherId: "",
  invoiceNumber: "",
  invoiceDate: "",
  dueDate: "",
  originalRevenue: "",
  deduction: "",
  deductionPercentage: "",
  finalRevenue: "",
  deductedAmount: "",
  publisherRevenue: "",
  selectedApps: [],
  status: "Pending",
  notes: "",
};

export default function InvoicesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";

  const [data, setData] = useState([]);
  const [form, setForm] = useState(initialFormState);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [publishers, setPublishers] = useState([]);
  const [apps, setApps] = useState([]);

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = isAdmin
        ? await invoiceService.getAll()
        : await invoiceService.getByPublisher(user.publisherId);
      setData(result);
    } catch (err) {
      toast.error("Failed to fetch invoices");
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, user?.publisherId]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Scroll lock for modal
  useEffect(() => {
    document.body.style.overflow = modalOpen ? "hidden" : "auto";
    return () => (document.body.style.overflow = "auto");
  }, [modalOpen]);

  const openModal = () => setModalOpen(true);

  const closeModal = useCallback(() => {
    setForm(initialFormState);
    setIsEditing(false);
    setEditId(null);
    setModalOpen(false);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    debugger
    const payload = {
      publisherId: form.publisherId,
      selectedApps: form.selectedApps,
      fromDate: form.fromDate,
      toDate: form.toDate,
      invoiceNumber: form.invoiceNumber || `INV-${Date.now()}`,
      originalRevenue: parseFloat(form.originalRevenue || 0),
      deduction: parseFloat(form.deduction || 0),
      deductionPercentage: parseFloat(form.deductionPercentage || 0),
      finalRevenue: parseFloat(form.finalRevenue || 0),
      deductedAmount: parseFloat(form.deductedAmount || 0),
      publisherRevenue: parseFloat(form.publisherRevenue || 0),
      paymentMethod: form.bankDetails?.method || null,
      bankDetails: form.bankDetails || null,
      status: form.status || "Pending",
      notes: form.notes || "",
    };
    try {
      if (isEditing) {
        await invoiceService.update(editId, payload);
        toast.success("Invoice updated successfully");
      } else {
        await invoiceService.create(payload);
        toast.success("Invoice created successfully");
      }
      fetchInvoices();
      closeModal();
    } catch {
      toast.error("Failed to save invoice");
    }
  };

  const handleDownloadInvoice = async (row) => {
   var response =  await invoiceService.downloadInvoice(row.id);
  };

  const handleEdit = (row) => {
    setForm(row);
    setEditId(row.id);
    setIsEditing(true);
    setModalOpen(true);
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Are you sure to delete this invoice?")) return;
    try {
      await invoiceService.delete(row.id);
      toast.success("Invoice deleted");
      fetchInvoices();
    } catch {
      toast.error("Failed to delete invoice");
    }
  };

  // Fetch properties (apps/web)
  const fetchProperties = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = isAdmin
        ? await propertyService.getAll()
        : await propertyService.getByPublisher(user.publisherId);
      setApps(result);
    } catch {
      toast.error("Failed to fetch properties");
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, user?.publisherId]);

  // Fetch publishers (for admin only)
  const loadPublishers = useCallback(async () => {
    try {
      const result = await PublisherApiService.getAllPublishers();
      setPublishers(result);
    } catch {
      toast.error("Failed to load publishers");
    }
  }, []);

  useEffect(() => {
    if (isAdmin) loadPublishers();
    fetchProperties();
  }, [fetchProperties, loadPublishers, isAdmin]);

  const modalBackdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.15 } },
    exit: { opacity: 0, transition: { duration: 0.15 } },
  };

  const modalContentVariants = {
    hidden: { opacity: 0, scale: 0.98, y: 8 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { type: "spring", damping: 25, stiffness: 400, duration: 0.25 },
    },
    exit: {
      opacity: 0,
      scale: 0.98,
      y: 8,
      transition: { duration: 0.15 },
    },
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
  try {
    debugger
    await invoiceService.updateStatus(invoiceId, newStatus);
    toast.success("Invoice status updated!");
    // optionally refresh data
    fetchInvoices();
  } catch {
    toast.error("Failed to update status");
  }
};

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="p-4 sm:p-6 space-y-4 sm:space-y-6"
    >
      <InvoicesHeader openModal={openModal} />

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <InvoicesTable
          data={data || []}
          downloadInvoice={handleDownloadInvoice}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isLoading={isLoading}
          onStatusChange={handleStatusChange}
        />
      </motion.div>

      <AnimatePresence>
        {modalOpen && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4"
            variants={modalBackdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <motion.div
              variants={modalContentVariants}
              className="bg-white rounded-xl shadow-lg w-full max-w-2xl p-6 relative"
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">
                  {isEditing ? "Edit Invoice" : "Add New Invoice"}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-black text-2xl transition-colors"
                  aria-label="Close modal"
                >
                  &times;
                </button>
              </div>

              <InvoicesForm
                form={form}
                onChange={setForm}
                onSubmit={handleSubmit}
                onCancel={closeModal}
                isEditing={isEditing}
                publishers={publishers}
                apps={apps}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
